import { isBlocked, STORY_STEP, type CollisionGrid, type StoryDirection } from '@rdd/contracts'

/**
 * O caminho do toque (docs/21 secao 5): o jogador toca no destino e o personagem anda ate la, um tile por
 * passo, nas quatro direcoes. A* sobre a grade de colisao, com a distancia de Manhattan como estimativa --
 * exata para quatro direcoes, entao o caminho achado e o mais curto.
 *
 * Devolve as DIRECOES, e nao os tiles: e o que o movimento consome, e o que o servidor de mundo recebe
 * (modo_historia/04), um `step` por vez.
 */

type Tile = { x: number; y: number }

const ORDER: readonly StoryDirection[] = ['up', 'down', 'left', 'right']

/** Teto de nos visitados: num mapa de 60x60 sao 3600, e o teto so existe para um mapa maior nao travar o quadro. */
const MAX_VISITED = 20_000

export function findPath(grid: CollisionGrid, from: Tile, to: Tile): StoryDirection[] | null {
  if (isBlocked(grid, to.x, to.y)) return null
  if (from.x === to.x && from.y === to.y) return []

  const key = (x: number, y: number): number => y * grid.width + x
  const heuristic = (x: number, y: number): number => Math.abs(x - to.x) + Math.abs(y - to.y)

  const cost = new Map<number, number>([[key(from.x, from.y), 0]])
  const cameFrom = new Map<number, { prev: number; dir: StoryDirection }>()
  // Fila de prioridade simples: o mapa e pequeno, e um heap seria codigo a mais para nenhum ganho visivel.
  const open: Array<{ x: number; y: number; f: number }> = [{ ...from, f: heuristic(from.x, from.y) }]
  let visited = 0

  while (open.length > 0 && visited < MAX_VISITED) {
    let best = 0
    for (let i = 1; i < open.length; i++) if ((open[i]?.f ?? Infinity) < (open[best]?.f ?? Infinity)) best = i
    const current = open.splice(best, 1)[0]
    if (current === undefined) break
    visited++
    const currentKey = key(current.x, current.y)
    if (current.x === to.x && current.y === to.y) return rebuild(cameFrom, currentKey, key(from.x, from.y))

    const g = cost.get(currentKey) ?? 0
    for (const dir of ORDER) {
      const nx = current.x + STORY_STEP[dir].dx
      const ny = current.y + STORY_STEP[dir].dy
      if (isBlocked(grid, nx, ny)) continue
      const nextKey = key(nx, ny)
      if ((cost.get(nextKey) ?? Infinity) <= g + 1) continue
      cost.set(nextKey, g + 1)
      cameFrom.set(nextKey, { prev: currentKey, dir })
      open.push({ x: nx, y: ny, f: g + 1 + heuristic(nx, ny) })
    }
  }
  return null
}

function rebuild(
  cameFrom: Map<number, { prev: number; dir: StoryDirection }>,
  end: number,
  start: number,
): StoryDirection[] {
  const dirs: StoryDirection[] = []
  let at = end
  while (at !== start) {
    const step = cameFrom.get(at)
    if (step === undefined) break
    dirs.push(step.dir)
    at = step.prev
  }
  return dirs.reverse()
}
